import { getTailwindColor } from '@ipfabric/frontend-shared-assets/tailwind/tailwindColors';
import type { FC } from 'react';
import type { SVGProps } from 'react';

type LogoColor = 'default' | 'white' | 'black' | 'shade';

const logoColorPalette: {
  [Key in LogoColor]: { primaryColor?: string; secondaryColor?: string };
} = {
  default: {
    primaryColor: getTailwindColor('logo.color-brand'),
    secondaryColor: getTailwindColor('logo.color-gray'),
  },
  white: {
    primaryColor: getTailwindColor('logo.white'),
    secondaryColor: getTailwindColor('logo.white'),
  },
  black: {
    primaryColor: getTailwindColor('logo.black'),
    secondaryColor: getTailwindColor('logo.black'),
  },
  shade: {
    primaryColor: getTailwindColor('logo.shade'),
    secondaryColor: getTailwindColor('logo.shade'),
  },
};

export type BaseLogoProps = SVGProps<SVGSVGElement> & {
  color?: LogoColor;
};

export type LogoProps = BaseLogoProps & {
  variant?: 'default' | 'compact';
};

export const Logo: FC<LogoProps> = ({
  color = 'default',
  variant = 'default',
  ...restProps
}) => {
  return variant === 'compact' ? (
    <CompactLogo color={color} {...restProps} />
  ) : (
    <DefaultLogo color={color} {...restProps} />
  );
};

const CompactLogo: FC<BaseLogoProps> = ({
  color = 'default',
  ...restProps
}) => {
  const { primaryColor, secondaryColor } = logoColorPalette[color];
  return (
    <svg
      viewBox='0 0 40 40'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      {...restProps}
    >
      <title>IP Fabric logo</title>
      <g id='Group'>
        <g id='Group_2'>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M30 26.6066C30 28.5886 28.1682 30.1501 26.0961 29.6697C24.9549 29.3994 24.024 28.4985 23.7537 27.3573C23.2733 25.2853 24.8348 23.4534 26.8168 23.4534C28.5886 23.4234 30 24.8648 30 26.6066Z'
            fill={secondaryColor}
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M16.7267 13.4234C16.7267 15.4054 14.8949 16.967 12.8228 16.4865C11.6817 16.2162 10.7507 15.3153 10.4805 14.1742C9.99998 12.1021 11.5615 10.2703 13.5435 10.2703C15.3153 10.2703 16.7267 11.6817 16.7267 13.4234Z'
            fill={primaryColor}
          />
        </g>
        <path
          d='M39.8498 3.5135V36.4865C39.8498 38.2282 38.4384 39.6396 36.6967 39.6396H16.9669L23.5135 33.0931C24.5045 33.6036 25.6456 33.9039 26.8468 33.9039C28.5886 33.9039 30.2102 33.2733 31.4715 32.2522C31.8318 31.9519 32.1922 31.5916 32.4925 31.2312C33.7537 29.6997 34.4144 27.6877 34.0841 25.4955C33.6336 22.4024 31.3213 19.97 28.2282 19.3994C26.4865 19.0691 24.8348 19.3694 23.4234 20.0901L20.06 16.7267C20.5706 15.7357 20.8709 14.6246 20.8709 13.4234C20.8709 12.2222 20.5706 11.021 20.03 9.99999L29.6697 0.360352H36.6666C38.4384 0.360352 39.8498 1.77176 39.8498 3.5135Z'
          fill={primaryColor}
        />
        <path
          d='M19.5195 26.6066C19.5195 27.8078 19.8198 28.9189 20.3303 29.9099L10.5706 39.6396H3.72371C3.48347 39.6396 3.24323 39.6096 2.97296 39.5495C1.83182 39.2793 0.900888 38.3784 0.630617 37.2372C0.570557 36.997 0.540527 36.7267 0.540527 36.4865V3.5135C0.540527 1.77176 1.95194 0.360352 3.69368 0.360352H23.2733L16.7868 6.84684C15.8258 6.36636 14.7147 6.09609 13.5736 6.09609C11.8318 6.09609 10.2102 6.69669 8.94894 7.74774C8.58858 8.04804 8.22822 8.37837 7.92791 8.76876C7.7177 9.009 7.7177 9.009 7.92791 8.76876C6.57656 10.4204 5.9159 12.6426 6.42641 15.015C7.02701 17.8378 9.24924 20.03 12.0721 20.5706C13.8438 20.9309 15.4955 20.6306 16.9069 19.9099L20.3303 23.3333C19.8198 24.3243 19.5195 25.4354 19.5195 26.6066Z'
          fill={secondaryColor}
        />
      </g>
    </svg>
  );
};

const DefaultLogo: FC<BaseLogoProps> = ({
  color = 'default',
  ...restProps
}) => {
  const { primaryColor, secondaryColor } = logoColorPalette[color];
  return (
    <svg
      viewBox='0 0 218.769 40'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      {...restProps}
    >
      <title>IP Fabric logo</title>
      <g>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M30 26.6066C30 28.5886 28.1682 30.1501 26.0961 29.6697C24.9549 29.3994 24.024 28.4985 23.7537 27.3573C23.2733 25.2853 24.8348 23.4534 26.8168 23.4534C28.5886 23.4234 30 24.8648 30 26.6066Z'
          fill={secondaryColor}
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M16.7267 13.4234C16.7267 15.4054 14.8949 16.967 12.8228 16.4865C11.6817 16.2162 10.7507 15.3153 10.4805 14.1742C9.99998 12.1021 11.5615 10.2703 13.5435 10.2703C15.3153 10.2703 16.7267 11.6817 16.7267 13.4234Z'
          fill={primaryColor}
        />
        <path
          d='M39.8498 3.5135V36.4865C39.8498 38.2282 38.4384 39.6396 36.6967 39.6396H16.9669L23.5135 33.0931C24.5045 33.6036 25.6456 33.9039 26.8468 33.9039C28.5886 33.9039 30.2102 33.2733 31.4715 32.2522C31.8318 31.9519 32.1922 31.5916 32.4925 31.2312C33.7537 29.6997 34.4144 27.6877 34.0841 25.4955C33.6336 22.4024 31.3213 19.97 28.2282 19.3994C26.4865 19.0691 24.8348 19.3694 23.4234 20.0901L20.06 16.7267C20.5706 15.7357 20.8709 14.6246 20.8709 13.4234C20.8709 12.2222 20.5706 11.021 20.03 9.99999L29.6697 0.360352H36.6666C38.4384 0.360352 39.8498 1.77176 39.8498 3.5135Z'
          fill={primaryColor}
        />
        <path
          d='M19.5195 26.6066C19.5195 27.8078 19.8198 28.9189 20.3303 29.9099L10.5706 39.6396H3.72371C3.48347 39.6396 3.24323 39.6096 2.97296 39.5495C1.83182 39.2793 0.900888 38.3784 0.630617 37.2372C0.570557 36.997 0.540527 36.7267 0.540527 36.4865V3.5135C0.540527 1.77176 1.95194 0.360352 3.69368 0.360352H23.2733L16.7868 6.84684C15.8258 6.36636 14.7147 6.09609 13.5736 6.09609C11.8318 6.09609 10.2102 6.69669 8.94894 7.74774C8.58858 8.04804 8.22822 8.37837 7.92791 8.76876C7.7177 9.009 7.7177 9.009 7.92791 8.76876C6.57656 10.4204 5.9159 12.6426 6.42641 15.015C7.02701 17.8378 9.24924 20.03 12.0721 20.5706C13.8438 20.9309 15.4955 20.6306 16.9069 19.9099L20.3303 23.3333C19.8198 24.3243 19.5195 25.4354 19.5195 26.6066Z'
          fill={secondaryColor}
        />
        <path
          d='M64.2643 33.9039H59.3093C59.039 33.9039 58.8288 33.6937 58.8288 33.4234V6.57658C58.8288 6.30631 59.039 6.0961 59.3093 6.0961H64.2643C64.5345 6.0961 64.7447 6.30631 64.7447 6.57658V33.4234C64.7447 33.6937 64.5345 33.9039 64.2643 33.9039Z'
          fill={primaryColor}
        />
        <path
          d='M88.5586 14.985C88.5586 17.5976 87.7477 19.7297 86.036 21.2913C84.3844 22.8529 82.1622 23.6336 79.3093 23.6336H75.1952C74.9249 23.6336 74.7147 23.8438 74.7147 24.1141V33.4234C74.7147 33.6937 74.5045 33.9039 74.2342 33.9039H69.2793C69.009 33.9039 68.7988 33.6937 68.7988 33.4234V6.57658C68.7988 6.30631 69.009 6.0961 69.2793 6.0961H79.3093C82.1622 6.0961 84.4144 6.87688 86.036 8.4985C87.7177 10.0601 88.5586 12.2523 88.5586 14.985ZM82.6426 14.985C82.6426 13.7838 82.3423 12.8829 81.7117 12.2823C81.1111 11.6517 80.2102 11.3514 79.0991 11.3514H75.2252C74.955 11.3514 74.7447 11.5616 74.7447 11.8318V17.8979C74.7447 18.1682 74.955 18.3784 75.2252 18.3784H79.0991C80.2402 18.3784 81.0811 18.1081 81.7117 17.5075C82.3423 16.9069 82.6426 16.0961 82.6426 14.985Z'
          fill={primaryColor}
        />
        <path
          d='M113.574 18.8589V23.1532C113.574 23.4234 113.363 23.6336 113.093 23.6336H106.396C106.126 23.6336 105.916 23.8438 105.916 24.1141V33.4234C105.916 33.6937 105.706 33.9039 105.435 33.9039H100.48C100.21 33.9039 100 33.6937 100 33.4234V6.57658C100 6.30631 100.21 6.0961 100.48 6.0961H117.477C117.748 6.0961 117.958 6.30631 117.958 6.57658V10.8709C117.958 11.1411 117.748 11.3514 117.477 11.3514H106.396C106.126 11.3514 105.916 11.5616 105.916 11.8318V17.9279C105.916 18.1982 106.126 18.4084 106.396 18.4084H113.093C113.363 18.4084 113.574 18.6186 113.574 18.8589Z'
          fill={primaryColor}
        />
        <path
          d='M193.634 33.9039H188.679C188.408 33.9039 188.198 33.6937 188.198 33.4234V6.57658C188.198 6.30631 188.408 6.0961 188.679 6.0961H193.634C193.904 6.0961 194.114 6.30631 194.114 6.57658V33.4234C194.114 33.6937 193.904 33.9039 193.634 33.9039Z'
          fill={primaryColor}
        />
        <path
          d='M180.18 22.8829C180.12 22.7327 180.15 22.5526 180.3 22.4625C180.871 22.1321 181.411 21.7417 181.892 21.2913C183.574 19.7297 184.384 17.5976 184.384 14.985C184.384 14.4444 184.354 13.9339 184.294 13.4535C184.024 11.4114 183.243 9.72973 181.892 8.46847C180.24 6.84685 178.018 6.06607 175.165 6.06607H165.135C164.865 6.06607 164.655 6.27628 164.655 6.54655V33.3934C164.655 33.6637 164.865 33.8739 165.135 33.8739H170.09C170.36 33.8739 170.571 33.6637 170.571 33.3934V24.0841C170.571 23.8138 170.781 23.6036 171.051 23.6036H173.694C173.934 23.6036 174.174 23.7538 174.294 23.994L178.799 33.6036C178.889 33.7838 179.039 33.8739 179.219 33.8739H184.925C185.285 33.8739 185.496 33.5135 185.345 33.1832L180.18 22.8829ZM177.538 17.5075C176.907 18.1081 176.036 18.3784 174.925 18.3784H171.051C170.781 18.3784 170.571 18.1682 170.571 17.8979V11.8318C170.571 11.5616 170.781 11.3514 171.051 11.3514H174.925C176.066 11.3514 176.967 11.6517 177.538 12.2823C178.018 12.7327 178.288 13.3333 178.408 14.1441C178.499 14.6847 178.499 14.6847 178.408 14.1441C178.438 14.4144 178.468 14.6847 178.468 14.985C178.468 16.0961 178.168 16.9069 177.538 17.5075Z'
          fill={primaryColor}
        />
        <path
          d='M130.12 6.0961H124.895C124.685 6.0961 124.504 6.24625 124.444 6.42643L115.976 33.2733C115.886 33.5736 116.096 33.9039 116.426 33.9039H121.532C121.742 33.9039 121.922 33.7538 121.982 33.5435L123.243 28.979C123.303 28.7688 123.483 28.6186 123.694 28.6186H131.261C131.471 28.6186 131.652 28.7688 131.712 28.979L132.973 33.5435C133.033 33.7538 133.213 33.9039 133.423 33.9039H138.529C138.859 33.9039 139.069 33.6036 138.979 33.2733L130.511 6.42643C130.511 6.24625 130.33 6.0961 130.12 6.0961ZM124.925 23.0931L127.207 14.8949C127.297 14.5946 127.718 14.5946 127.808 14.8949L130.09 23.0931C130.15 23.3333 129.97 23.6036 129.7 23.6036H125.255C125.045 23.6036 124.835 23.3634 124.925 23.0931Z'
          fill={primaryColor}
        />
        <path
          d='M160.33 21.8018C159.88 21.021 159.279 20.4204 158.529 19.9099C158.348 19.7898 158.348 19.5496 158.498 19.4294C158.799 19.1291 159.219 18.7387 159.429 18.4685C159.58 18.2883 159.7 18.0781 159.82 17.8679C160.511 16.7568 160.841 15.4655 160.841 13.964C160.841 11.5315 160.03 9.60961 158.378 8.22823C156.757 6.81682 154.565 6.12613 151.742 6.12613H141.892C141.622 6.12613 141.411 6.33634 141.411 6.60661V33.4535C141.411 33.7237 141.622 33.9339 141.892 33.9339H152.673C155.375 33.9339 157.538 33.2432 159.069 31.7718C160.601 30.3604 161.381 28.3183 161.381 25.7658C161.351 24.2342 160.991 22.9129 160.33 21.8018ZM147.327 11.8318C147.327 11.5616 147.538 11.3514 147.808 11.3514H151.652C152.763 11.3514 153.574 11.5916 154.114 12.0721C154.144 12.0721 154.144 12.1021 154.174 12.1321C154.655 12.6426 154.925 13.3333 154.925 14.2342C154.925 15.2553 154.655 15.976 154.114 16.5465C153.604 17.0571 152.793 17.3273 151.652 17.3273H147.808C147.538 17.3273 147.327 17.1171 147.327 16.8468V11.8318ZM154.595 27.8378C154.084 28.3784 153.273 28.6487 152.132 28.6487H147.778C147.508 28.6487 147.297 28.4384 147.297 28.1682V22.8829C147.297 22.6126 147.508 22.4024 147.778 22.4024H152.132C153.243 22.4024 154.054 22.6727 154.625 23.1532C154.655 23.1832 154.655 23.1832 154.685 23.2132C155.165 23.7538 155.435 24.4444 155.435 25.4054C155.435 26.4565 155.165 27.2372 154.595 27.8378Z'
          fill={primaryColor}
        />
        <path
          d='M211.351 27.8379C210.691 28.5285 209.67 28.8889 208.288 28.8889C206.757 28.8889 205.646 28.4685 204.955 27.5676C204.264 26.6667 203.904 25.1351 203.904 23.033V16.997C203.904 14.8949 204.264 13.3634 204.955 12.4625C205.646 11.5616 206.757 11.1412 208.288 11.1412C209.67 11.1412 210.691 11.5015 211.351 12.1922C211.952 12.7928 212.312 13.6336 212.402 14.6547C212.432 14.8949 212.643 15.1051 212.883 15.1051H217.778C218.048 15.1051 218.258 14.8949 218.258 14.6246C218.168 11.8619 217.267 9.75977 215.556 8.19821C213.754 6.60662 211.291 5.76578 208.198 5.76578C201.411 5.76578 197.988 9.51953 197.988 16.9369V23.1231C197.988 30.5406 201.441 34.2943 208.198 34.2943C211.291 34.2943 213.754 33.4835 215.556 31.8619C217.237 30.3303 218.138 28.1982 218.258 25.4354C218.258 25.1652 218.048 24.955 217.778 24.955H212.883C212.643 24.955 212.432 25.1351 212.402 25.4054C212.312 26.3964 211.952 27.2072 211.351 27.8379Z'
          fill={primaryColor}
        />
      </g>
    </svg>
  );
};
